import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, startWith } from "rxjs";

@Injectable({ providedIn: 'root' })
/**
 * Service for managing the application's layout state, specifically fullscreen mode functionality.
 *
 * This service provides a reactive approach to controlling when the application should display
 * in fullscreen mode based on route prefixes. It combines Angular Router events with internal
 * state management to automatically determine and emit the current fullscreen status.
 *
 * @remarks
 * The service automatically tracks navigation events and compares the current URL against
 * the configured fullscreen route prefix. This enables automatic layout switching without
 * manual intervention on each route change.
 */
export class LayoutStateService {

  // Holds the route prefix that requested fullscreen (null = none)
  private fullscreenRoutePrefix$ = new BehaviorSubject<string | null>(null);

  // Manual fullscreen override (true = fullscreen regardless of route)
  private manualFullscreen$ = new BehaviorSubject<boolean>(false);

  /**
   * Observable that emits a boolean indicating whether the application is currently in fullscreen mode.
   *
   * The fullscreen state is determined by two conditions:
   * - Manual fullscreen mode has been explicitly enabled
   * - The current route starts with a configured fullscreen route prefix
   *
   * The observable combines three sources:
   * - `fullscreenRoutePrefix$`: Observable of the current fullscreen route prefix
   * - `manualFullscreen$`: Observable indicating manual fullscreen toggle state
   * - Router navigation events: Triggers recalculation on route changes
   *
   * @remarks
   * - Uses `startWith(null)` to ensure immediate evaluation on subscription
   * - Employs `distinctUntilChanged()` to prevent unnecessary emissions when the fullscreen state hasn't changed
   * - Automatically updates when navigation occurs or when the fullscreen route prefix changes
   *
   * @returns An observable that emits `true` when in fullscreen mode, `false` otherwise.
   * The observable only emits when the fullscreen state actually changes.
   */
  readonly isFullscreen$ = combineLatest([
    this.fullscreenRoutePrefix$,
    this.manualFullscreen$,
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      startWith(null)
    )
  ]).pipe(
    map(([prefix, manual]) => {
      if (manual) return true;
      return !!prefix && this.router.url.startsWith(prefix);
    }),
    distinctUntilChanged()
  );

  constructor(private router: Router) {}

  /**
   * Enters fullscreen mode by clearing the fullscreen route prefix and setting manual fullscreen to true.
   * This method triggers the fullscreen state by updating the relevant observables.
   */
  enterFullScreen() {
    this.fullscreenRoutePrefix$.next(null);
    this.manualFullscreen$.next(true);
  }

  /**
   * Enters fullscreen mode for a specific route prefix.
   *
   * @param routePrefix - The route prefix to associate with the fullscreen mode
   */
  enterFullscreenForRoute(routePrefix: string) {
    this.manualFullscreen$.next(false);
    this.fullscreenRoutePrefix$.next(routePrefix);
  }

  /**
   * Enters fullscreen mode for the current route by setting the fullscreen route prefix
   * to the current router URL. This allows the application to track which route
   * should be displayed in fullscreen mode.
   */
  enterFullscreenForCurrentRoute() {
    this.manualFullscreen$.next(false);
    this.fullscreenRoutePrefix$.next(this.router.url);
  }

  /**
   * Enters fullscreen using a computed boundary instead of the exact current URL.
   * The boundary is stable across sibling (dynamic last segment) navigations.
   * Optionally accepts a callback to customize boundary derivation.
   *
   * @param compute Optional callback (segments, rawUrl) => boundary string.
   *                If omitted, a default heuristic trims a dynamic last segment
   *                (numeric ID or GUID-like token).
   *
   * @returns The boundary string used (if you need to log or store it).
   */
  enterFullscreenWithBoundary(compute?: (segments: string[], rawUrl: string) => string): string {
    this.manualFullscreen$.next(false);
    const boundary = this.computeFullscreenBoundary(compute);
    this.fullscreenRoutePrefix$.next(boundary);
    return boundary;
  }
  /**
   * Exits fullscreen mode by clearing the fullscreen route prefix.
   * Sets the fullscreen route prefix observable to null, effectively
   * disabling fullscreen layout state.
   */
  exitFullscreen() {
    this.fullscreenRoutePrefix$.next(null);
    this.manualFullscreen$.next(false);
  }

  /**
   * Determines if the application is currently in fullscreen mode synchronously.
   *
   * @returns {boolean} True if in fullscreen mode either manually set or when the current route
   * starts with the configured fullscreen route prefix, false otherwise.
   *
   * @remarks
   * This method checks two conditions:
   * 1. Manual fullscreen mode is enabled via `manualFullscreen$` observable
   * 2. Current router URL starts with the fullscreen route prefix from `fullscreenRoutePrefix$`
   *
   * Manual fullscreen mode takes precedence over route-based fullscreen detection.
   *
   * Use isFullscreenSync() only when you need an immediate value without setting up (or waiting for) an observable subscription.
   * Typical places:
   * - Route guards (CanActivate / CanDeactivate) deciding navigation.
   * - HostListeners or event handlers needing a quick conditional (e.g. keydown, resize).
   * - Template helpers in rare cases (though prefer async pipe).
   * - Non-Angular code (e.g. standalone utility or integration code) where Rx subscription is awkward.
   * - During destruction (ngOnDestroy) to avoid relying on asynchronous emissions.
   * - Conditional early returns before kicking off heavy async work.
   *
   * @see isFullscreen$ for the reactive equivalent of this method.
   */
  isFullscreenSync(): boolean {
    const manual = this.manualFullscreen$.value;
    const prefix = this.fullscreenRoutePrefix$.value;
    if (manual) return true;
    return !!prefix && this.router.url.startsWith(prefix);
  }

  /**
   * Computes the fullscreen boundary URL based on the current router URL.
   *
   * This method analyzes the current route segments to determine an appropriate
   * boundary URL for fullscreen operations. It can handle dynamic routes (containing
   * numeric IDs or hexadecimal identifiers) by excluding the dynamic segment from
   * the boundary calculation.
   *
   * @param custom - Optional custom function to override the default boundary computation.
   *                 Receives the route segments array and raw URL as parameters.
   *                 Should return a boundary path string.
   *
   * @returns The computed fullscreen boundary URL as a string, always starting with '/'.
   *          Returns the raw URL if no segments are found, or a computed path based on
   *          the route segments (excluding dynamic segments like IDs).
   */
  private computeFullscreenBoundary(custom?: (segments: string[], rawUrl: string) => string): string {
    const rawUrl = this.router.url || '/';
    const tree = this.router.parseUrl(rawUrl);
    const segments = tree.root.children['primary']?.segments.map(s => s.path) ?? [];

    if (custom) {
      const result = custom(segments, rawUrl);
      return result.startsWith('/') ? result : '/' + result;
    }

    if (!segments.length) {
      return rawUrl || '/';
    }

    const last = segments[segments.length - 1];
    const isDynamic =
      /^\d+$/.test(last) ||
      /^[0-9a-fA-F]{8,}(-[0-9a-fA-F]{4,})*$/.test(last) ||  // GUID-like: 8+ hex, optional hyphen-separated groups
      /^[0-9a-fA-F]{6,}$/.test(last);  // Hex without hyphens
    const boundarySegments = isDynamic ? segments.slice(0, -1) : segments;
    return '/' + boundarySegments.join('/');
  }
}
